import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DARK_LOGO = 'https://customer-assets.emergentagent.com/job_inventory-core-11/artifacts/43j2lsb0_Tirth-Logo-Final-File-01.png';
const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GOOGLE_ERRORS = {
  no_account: (email) => `${email || 'That Google account'} is not assigned to any ERP user. Ask an admin to add it under Users → Edit → "Google sign-in e-mail".`,
  access_denied: () => 'Google refused the sign-in (access_denied). Ask the admin to publish the OAuth app or add you as a test user.',
};

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.7 1.2 9.2 3.6l6.9-6.9C35.9 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l8 6.2C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
    <path fill="#FBBC05" d="M10.6 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-8-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l8-6.2z" />
    <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.2 0-11.5-4.1-13.4-9.8l-8 6.2C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [google, setGoogle] = useState(null);
  const { login } = useAuth();

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    axios.get(`${API}/google/login/start`).then((r) => setGoogle(r.data)).catch(() => setGoogle({ enabled: false }));
    const q = new URLSearchParams(window.location.search);
    if (q.get('google') === 'error') {
      const reason = q.get('reason') || 'unknown';
      setError((GOOGLE_ERRORS[reason] || (() => `Google sign-in failed: ${reason}`))(q.get('email')));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const googleLogin = () => { if (google?.url) window.location.href = google.url; };

  // 401/403 come from the ERP itself (JSON `detail`). Anything else — no response, or a 5xx /
  // HTML page from the edge while the server restarts during a deploy — must never be shown raw.
  const describeLoginError = (err) => {
    const status = err.response?.status;
    const data = err.response?.data;
    const detail = data && typeof data === 'object' ? data.detail : null;
    if (status && status < 500 && typeof detail === 'string') return { message: detail, transient: false };
    if (status === 429) return { message: 'Too many attempts. Please wait a minute and try again.', transient: false };
    const ref = data && typeof data === 'object' && data.request_id ? ` · ref ${data.request_id}` : '';
    return {
      transient: true,
      message: !status
        ? 'Could not reach the server. Check your connection and try again.'
        : `The server is restarting or temporarily unavailable (${status}). Please try again in a few seconds.${ref}`,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      try {
        await login(email, password);
      } catch (err) {
        const first = describeLoginError(err);
        if (!first.transient) throw err;
        // one automatic retry after a short pause — covers the ~30 s pod restart during deploys
        setError('Server is restarting — retrying…');
        await new Promise((r) => setTimeout(r, 3000));
        await login(email, password);
        setError('');
      }
    } catch (err) {
      setError(describeLoginError(err).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Tirth Industries manufacturing"
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <div className="flex items-center gap-4 mb-10">
            <img src={DARK_LOGO} alt="Tirth Industries" className="w-14 h-14 rounded-xl object-contain" />
            <div>
              <div className="font-heading font-bold text-white text-2xl">Tirth Industries</div>
              <div className="text-amber-400 text-sm font-medium">Seasoning & Spices · ERP</div>
            </div>
          </div>
          <h1 className="font-heading font-semibold text-white text-4xl leading-tight mb-4">
            Manufacturing
            <span className="text-red-500"> Precision.</span>
            <br />
            <span className="text-amber-400">Smart</span> Control.
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md">
            Full-stack ERP for Tirth Industries. FIFO inventory, production batching,
            Tally-style accounting, GST compliance — all in one place.
          </p>
          <div className="mt-8 flex gap-8">
            {[
              { label: 'FIFO Inventory', value: 'Lot Tracking' },
              { label: 'TI Assistant', value: 'AI Powered' },
              { label: 'GST Ready', value: 'India Compliant' },
            ].map(item => (
              <div key={item.label}>
                <div className="text-red-400 font-mono text-xs font-bold">{item.value}</div>
                <div className="text-zinc-500 text-xs mt-0.5">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#09090B]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img src={DARK_LOGO} alt="TI" className="w-10 h-10 rounded-lg object-contain" />
            <div className="font-heading font-bold text-white text-lg">Tirth Industries ERP</div>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-zinc-50">Welcome back</h2>
            <p className="text-zinc-400 text-sm mt-1">Sign in to Tirth Industries ERP</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-1.5">Email Address</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@tirthindustries.com"
                autoComplete="username"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-zinc-50 text-sm placeholder-zinc-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 pr-12 text-zinc-50 text-sm placeholder-zinc-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 transition-colors p-1 rounded"
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password && password.length > 0 && (
                <div className="text-[10px] text-zinc-500 mt-1" data-testid="password-length">
                  {password.length} character{password.length === 1 ? '' : 's'} entered
                </div>
              )}
            </div>

            {error && (
              <div data-testid="login-error" className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-500 disabled:bg-red-600/50 text-white font-heading font-semibold rounded-xl px-4 py-3 text-sm transition-colors mt-2"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {google?.enabled && (
            <div className="mt-5">
              <div className="flex items-center gap-3 text-[11px] text-zinc-500 mb-3"><span className="flex-1 h-px bg-zinc-800" />or<span className="flex-1 h-px bg-zinc-800" /></div>
              <button type="button" onClick={googleLogin} className="w-full flex items-center justify-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-100 font-medium rounded-xl px-4 py-3 text-sm transition-colors" data-testid="google-login-btn">
                <GoogleG /> Continue with Google
              </button>
              <p className="text-[10px] text-zinc-600 mt-2 text-center">Works for ERP accounts whose e-mail or connected Google account matches.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
